import { GridApi } from '@ag-grid-community/core';
import { inject, Injectable } from '@angular/core';
import { groupBy } from 'lodash-es';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';

import {
  batchOverrideExpenseSourcesMutation,
  ExpenseSourceType,
  GqlService,
} from '@shared/services/gql.service';
import { batchPromises } from '@shared/utils/batch-promises';

import { ExpenseSource, QuarterCloseAdjustmentGridData } from '../quarter-close-adjustments.model';

type SourceChangedRow = {
  hasAutoAdjustment: boolean;
  id: string;
  newValue: ExpenseSourceType;
  vendorId: string;
};

@Injectable()
export class ExpenseSourceService {
  private readonly gqlService = inject(GqlService);

  private readonly maxBatchSize = 100;

  batchOverrideExpenseSources(
    selectedVendors: string[],
    period: string,
    overriddenSourceRows: SourceChangedRow[]
  ) {
    const groupedSourceChangedRows = groupBy(overriddenSourceRows, 'vendorId');

    const overriddenSourcePromises: Promise<
      GraphqlResponse<batchOverrideExpenseSourcesMutation[]>
    >[] = [];

    selectedVendors.forEach((vendorId) => {
      const overriddenSourcesVendor = groupedSourceChangedRows[vendorId];

      if (overriddenSourcesVendor) {
        for (let j = 0; j < overriddenSourcesVendor.length; j += this.maxBatchSize) {
          overriddenSourcePromises.push(
            firstValueFrom(
              this.gqlService.batchOverrideExpenseSources$({
                organization_id: vendorId,
                overrides: overriddenSourcesVendor
                  .slice(j, j + this.maxBatchSize)
                  .map(({ id, newValue }) => ({
                    activity_id: id,
                    source: newValue,
                  })),
                period,
              })
            )
          );
        }
      }

      void batchPromises(overriddenSourcePromises, (p) => p);
    });
  }

  batchRemoveBudgetExpenses(deletedManuals: string[]) {
    const manualsToBeDeletedBatches: string[][] = [];

    for (let j = 0; j < deletedManuals.length; j += this.maxBatchSize) {
      manualsToBeDeletedBatches.push(deletedManuals.slice(j, j + this.maxBatchSize));
    }

    const promises = manualsToBeDeletedBatches.map((p) =>
      firstValueFrom(this.gqlService.batchRemoveBudgetExpenses$(p))
    );

    return batchPromises(promises, (p) => p, this.maxBatchSize);
  }

  getSourceChangedRows(
    gridApi: GridApi<QuarterCloseAdjustmentGridData>,
    sourceChangedRows: Set<string>,
    manualsToBeDeleted: Set<string>,
    expenseSourceReverseMapping: Record<ExpenseSource, ExpenseSourceType>
  ): { deletedManuals: string[]; overriddenSourceRows: SourceChangedRow[] } {
    const sourcesRows: SourceChangedRow[] = [];
    const deletedManuals: string[] = [];

    gridApi.forEachNode((row) => {
      if (row.data) {
        if (sourceChangedRows.has(row.data.activity_id)) {
          sourcesRows.push({
            hasAutoAdjustment: !!row.data.historical_adjustment,
            id: row.data.activity_id,
            newValue: expenseSourceReverseMapping[row.data.tma_source as ExpenseSource],
            vendorId: row.data.organization_id,
          });
        }

        if (manualsToBeDeleted.has(row.data.activity_id) && row.data.manual_adjustment?.id) {
          deletedManuals.push(row.data.manual_adjustment.id);
        }
      }
    });

    return { deletedManuals, overriddenSourceRows: sourcesRows };
  }
}
